<template>
    <div>
        <div class="row">
            <div class="col-lg-12 mb-3">
                <b-breadcrumb :items="breadcrumbItems" class="breadcrumb1 breadcrumbCustom border"/>
                <b-card :header="$t('general.users.welders.title')" header-tag="h3" class="bg-default-card">
                    <data-table
                        v-if="tableLoaded"
                        :exportable="this.$store.getters.hasPermission(['welders'], ['read'])"
                        :importable="true"
                        title=""
                        :rows="user.getData.rowData"
                        :columns="user.columnData"
                        :perPage="perPage"
                        :rowsCount="rowsCount"
                        :currentPage="currentPage"
                        :pageCount="pageCount"
                        @exportButtonClick="onExportButton"
                        @rowClickDelete="onDeleteRow"
                        @rowClickEdit="onEditRow"
                        @changePage="changePage"
                        @searchEvent="searchEvent"
                        @orderByEvent="orderByEvent"
                    >
                    </data-table>

                    <b-btn class="btn btn-default mt-2 mr-2" @click="onAddRow" v-if="this.$store.getters.hasPermission(['welders'], ['create'])">
                        <i class="fa fa-plus-circle mr-1"></i>{{ $t('general.users.welders.create_button') }}
                    </b-btn>

                    <span v-if="this.$store.getters.hasPermission(['welders'], ['create', 'update'])">
                        <label v-if="!this.$store.state.ajax_in_progress" class="btn btn-primary mt-3" @click="$refs.importExcel.value = ''">
                            <span><i class="fa fa-upload mr-1"></i> {{ $t('general.users.welders.import_button') }}</span>
                            <input type="file" name="file" style="display:none" ref="importExcel" @change="onImportButton($event.target.name, $event.target.files)" accept="application/*"/>
                        </label>
                        <label v-else class="btn btn-primary mt-3 disabled">
                            <span><i class="fa fa-upload mr-1"></i> {{ $t('general.ajax_in_progress_please_wait') }}</span>
                        </label>
                    </span>
                </b-card>
            </div>
        </div>

        <b-modal ref="saveModal" :ok-title="$t('misc.ok')" :cancel-title="$t('misc.cancel')" :title="actionTitle" size="lg" @ok="saveAction" :ok-disabled="$store.state.ajax_in_progress || (row && !$store.getters.hasPermission(['users'], ['update']))">
            <div v-if="!selectedUserRow">
                <label>{{ $t('general.users.welders.select_user') }}</label>
                <multiselect v-model="selectedWelder"
                    :options="currentWelder"
                    :clear-on-select="true"
                    :hide-selected="false"
                    :loading="isWelderLoading"
                    :preserve-search="true"
                    placeholder=""
                    track-by="id"
                    label="fullname"
                    :selectLabel="''"
                    @search-change="onWelderSearch"
                >
                    <template slot="clear" slot-scope="props">
                        <div 
                            v-if="selectedWelder != null" 
                            class="multiselect__clear" 
                            @mousedown.prevent.stop="clearSelectedWelder"
                        >
                        </div>
                    </template>
                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                </multiselect>
            </div>

            <div v-if="selectedUserRow != null" class="row">
                <div class="col-lg-12">
                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.welder_no') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <input type="text" maxlength="50" class="form-control" v-model="user.model.welder_no">
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-lg-4">
                            <label>{{ $t('table.welder_status') }}</label>
                        </div>
                        <div class="col-lg-8">
                            <select class="form-control" v-model="user.model.welder_status">
                                <option v-for="status in statuses" :value="status.code" :key="status.code">{{status.name}}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <hr v-if="((user.getData.getFiles.length > 0 && hasFilesReadPermission) || hasFilesCreatePermission) && selectedUserRow != null">
            <div class="row mt-2" v-if="user.getData.getFiles.length > 0 && hasFilesReadPermission">
                <div class="col-lg-12">
                    <label>{{ $t('table.uploaded_files') }}</label>
                </div>
                <div class="col-lg-6" v-for="file in user.getData.getFiles" :key="file.id">
                    <div class="card p-2">
                        <div style="width: 100%; display: block">
                            <p 
                                class="float-left customLink" 
                                @click="onDownloadFile(file.id)" 
                                :title="file.file_name"
                            >
                                {{file.file_name}}
                            </p>
                            <p 
                                v-if="hasFilesDeletePermission"
                                class="float-right p-0 m-0"
                            >
                                <i class='fa fa-2x fa-trash mt-2 text-danger sortable cursor-pointer' @click="onDeleteFile(file.id)"></i>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-2" v-if="hasFilesCreatePermission && selectedUserRow != null">
                <div class="col-lg-12">
                    <div class="row mb-3">
                        <div class="col-lg-12">
                            <label v-if="row && user.getData.getFiles.length > 0">{{ $t('table.upload_more_files') }}</label>
                            <label v-else>{{ $t('table.upload_files') }}</label>
                        </div>
                        <div class="col-lg-12">
                            <vue-dropzone class="dropzone" ref="document" id="dropzone" :options="user.dropzoneOptions"></vue-dropzone>
                        </div>
                    </div>
                </div>
            </div>
            <hr v-if="((user.getData.getFiles.length > 0 && hasFilesReadPermission) || hasFilesCreatePermission) && selectedUserRow != null">

            <b-collapse v-model="cert.showEditForm" id="certForm">
                <hr>
                <certificate-modal :value.sync="cert.model" @updateWps="updateFields"></certificate-modal>
                <div class="pull-right mb-2">
                    <button class="btn btn-success mr-2" @click="actionUpdateCertificate">
                        <i class="fa fa-floppy-o" aria-hidden="true"></i> {{ $t('misc.save_certificate') }}
                    </button>
                    <button class="btn btn-danger" @click="cert.showEditForm=false">
                        {{ $t('misc.cancel_edit') }}
                    </button>
                </div>
            </b-collapse>

            <div v-if="selectedUserRow != null">
                <data-table
                    :exportable="false"
                    :importable="false"
                    title=""
                    :search = "false"
                    :paginate = "false"
                    :rows="cert.getData.rowData"
                    :columns="cert.columnData"
                    @exportButtonClick="onExportButtonCert"
                    @rowClickDelete="onDeleteCertificateRow"
                    @rowClickEdit="onEditCertificateRow"
                    >
                </data-table>
            </div>

            <div v-if="selectedUserRow != null">
                <i class="btn pull-right mt-2 fa fa-2x fa-plus-square" style="padding: 0 0 5px 0;" @click="onAddCertificateRow"></i>
            </div>
        </b-modal>

        <b-modal ref="addCertificateModal" :ok-title="$t('misc.ok')" :cancel-title="$t('misc.cancel')" :title="actionTitleCertificate" size="lg" @ok="actionInsertCertificate" @hidden="onAddCertificateHidden">
            <certificate-modal :value.sync="cert.model" @updateWps="updateFields"></certificate-modal>
        </b-modal>

        <confirm-dialog ref="deleteModal" :title="actionTitleDelete" @buttonClick="deleteAction">
            {{ delete_message }}
        </confirm-dialog>

        <confirm-dialog ref="deleteCertificateModal" :title="actionTitleCertificate" @buttonClick="actionDeleteCertificate">
            {{ $t('general.users.welders.certificate_delete_question') }}
        </confirm-dialog>
    </div>
</template>

<script>
import Vue from 'vue';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import http from '@/libs/http';
import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import miniToastr from 'mini-toastr';
import Multiselect from 'vue-multiselect';

import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

import vue2Dropzone from 'vue2-dropzone';
import '@/assets/css/vue2Dropzone.css';

import CertificateModal from '@/components/global/modals/CertificateModal.vue';

export default {
    name: 'welders',
    components: {
        DataTable,
        ConfirmDialog,
        notificationIcons,
        flatPickr,
        Multiselect,
        CertificateModal,
        vueDropzone: vue2Dropzone,
    },
    data() {
        return {
            breadcrumbItems: [
                {
                    text: Vue.i18n.translate('general.dashboard.title'),
                    to: '/',
                },
                {
                    text: Vue.i18n.translate('general.users.welders.title'),
                    active: true
                }
            ],
            tableName: 'user',
            tableNameTranslated: Vue.i18n.translate('general.users.welders.title_modal'),
            row: null,
            
            tableLoaded: false,
            perPage: 0,
            rowsCount: 0,
            pageCount: 1,
            currentPage: 1,
            sortType: 'asc',
            sortBy: 'id',
            searchInput: '',

            actionTitle: '',
            actionTitleDelete: '',
            actionTitleCertificate: '',
            delete_message: '',

            selectedUserRow: null,
            selectedCertRow: null,

            selectedWelder: null,
            currentWelder: [],
            isWelderLoading: false,

            hasFilesReadPermission: false,
            hasFilesCreatePermission: false,
            hasFilesDeletePermission: false,

            user: {
                file_id: null,
                isFileDelete: false,
                dropzoneOptions: {
                    url: '/',
                    thumbnailWidth: 150,
                    addRemoveLinks: true,
                    autoProcessQueue: false,
                    uploadMultiple: true,
                    dictDefaultMessage: '<i class="fa fa-cloud-upload"></i>' + ' ' + Vue.i18n.translate('table.upload_files'),
                    maxFiles: 3,
                },
                model: {
                    welder_no: null,
                    welder_status: null,
                },
                getData: {
                    rowData: [],
                    getNonWelders: [],
                    getFiles: [],
                },
                columnData: [
                    {
                        label: Vue.i18n.translate('misc.actions'),
                        field: '',
                        width: '90px',
                        showDeleteButton: true,
                        showEditButton: true
                    },
                    {
                        label: Vue.i18n.translate('table.firstName'),
                        field: 'firstname',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.lastName'),
                        field: 'lastname',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.id_no'),
                        field: 'user_id_no',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.role'),
                        field: 'role',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.welder_no'),
                        field: 'welder_no',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.welder_status'),
                        field: 'welder_statusV',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.certificateNo'),
                        field: 'last_certificate_no',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.last_certificate_expiration_date'),
                        field: 'last_certificate_expiration_date',
                        numeric: false,
                        html: false,
                    }
                ]
            },
            cert: {
                showEditForm: false,
                model: {
                    certificate_no: null,
                    user_id: null,
                    qualification: null,
                    number: null,
                    welding_process: null,
                    welding_type: null,
                    group_filler_material: null,
                    range_of_filler_material: null,
                    wps_id: null,
                    material: null,
                    dia: null,
                    thickness: null,
                    remark: null,
                    expire_at: null,
                    expire_remark: null,
                    selectedWps: null,
                },
                getData: {
                    rowData: [],
                },
                columnData: [
                   {
                        label: Vue.i18n.translate('misc.actions'),
                        field: '',
                        width: '90px',
                        showDeleteButton: true,
                        showEditButton: true
                    },
                    {
                        label: Vue.i18n.translate('table.certificateNo'),
                        field: 'certificate_no',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.qualification'),
                        field: 'qualification',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.welding_process'),
                        field: 'welding_process',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.welding_type'),
                        field: 'welding_type',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.group_filler_material'),
                        field: 'group_filler_material',
                        numeric: false,
                        html: false,
                    },
                    {
                        label: Vue.i18n.translate('table.certificate_valid_days'),
                        field: 'certificate_valid_days',
                        numeric: false,
                        html: false,
                    }
                ]
            },
        }
    },
    mounted () {
        this.onLoadTable();

        this.hasFilesReadPermission   = this.$store.getters.hasPermission(['files'], ['read']);
        this.hasFilesCreatePermission = this.$store.getters.hasPermission(['files'], ['create']);
        this.hasFilesDeletePermission = this.$store.getters.hasPermission(['files'], ['delete']);
    },
    computed: {
        statuses: {
            get: function () {
                let statuses = this.$store.getters.getWelderStatuses;
                return statuses;
            }
        }
    },
    watch: {
        statuses: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        }
    },
    methods: {
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        searchEvent (searchInput) {
            this.searchInput = searchInput;
            this.onLoadTable();
        },
        orderByEvent (column, sort_type) {
            this.sortType = sort_type;
            this.sortBy = column.field;
            this.onLoadTable();
        },
        changePage (page) {
            this.currentPage = page;
            this.onLoadTable();
        },
        onLoadTable () {
            this.user.getData.rowData = [];
            let data = http.route('user/getall?get_all_welders=1&get_all=1&search=' + this.searchInput + '&filters[is_welder]=1&page=' + this.currentPage + '&sort_by=' + this.sortBy + '&sort_order=' + this.sortType).get();
            data.then(response => {
                if (response.isError == false) {
                    this.perPage     = response.pagination.limit;
                    this.rowsCount   = response.pagination.totalEntries;
                    this.pageCount   = response.pagination.totalPages;
                    this.currentPage = response.pagination.currentPage;
                    
                    var data = response.data;
                    data.forEach(field => {
                        if (typeof this.statuses[field.welder_status] != 'undefined') {
                            field['welder_statusV'] = this.statuses[field.welder_status].name;
                        }
                        
                        if (field.last_certificate_valid_days == 1) {
                            field.last_certificate_valid_days = field.last_certificate_valid_days + ' ' + Vue.i18n.translate('table.day');
                        } else if (field.last_certificate_valid_days > 1) {
                            field.last_certificate_valid_days = field.last_certificate_valid_days + ' ' + Vue.i18n.translate('table.days');
                        } else if (field.last_certificate_valid_days == 'expired') {
                            field.last_certificate_valid_days = Vue.i18n.translate('misc.expired');
                        } else {
                            field.certificate_valid_days = null;
                        }
                    });
                    this.user.getData.rowData = data;
                    this.tableLoaded = true;
                } else {
                    let msg = '';
                    Object.keys(response.errorMessage).forEach(key => {
                        msg += Vue.i18n.translate(response.errorMessage[key]) + "\n";
                    });
                    this.errorMsg(msg);
                }
            });
        },
        clearSearches () {
            this.selectedWelder = null;
            this.currentWelder = [];
            this.isWelderLoading = false;

            this.user.getData.getFiles = [];
            this.$nextTick(() => {
                if (typeof this.$refs.document != 'undefined') {
                    this.$refs.document.removeAllFiles();
                }
            });
        },
        onAddRow () {
            this.actionTitle = Vue.i18n.translate('modal.general.create') + ' ' + this.tableNameTranslated;
            this.selectedUserRow = null;
            this.cert.showEditForm = false;
            this.clearSearches();

            this.user.model = {
                welder_no: null,
                welder_status: null,
            };
            this.cert.model = {
                certificate_no: null,
                user_id: null,
                qualification: null,
                number: null,
                welding_process: null,
                welding_type: null,
                group_filler_material: null,
                range_of_filler_material: null,
                wps_id: null,
                material: null,
                dia: null,
                thickness: null,
                remark: null,
                expire_at: null,
                expire_remark: null,
                selectedWps: null,
            };

            this.$refs.saveModal.show();
        },
        onEditRow (row) {
            this.actionTitle = Vue.i18n.translate('modal.general.edit') + ' ' + this.tableNameTranslated;
            this.selectedUserRow = row;
            this.showEditWelder();
        },
        showEditWelder() {
            this.actionTitle = Vue.i18n.translate('modal.general.edit') + ' ' +  this.selectedUserRow.fullname;
            this.clearSearches();

            this.selectedWelder = null;
            this.cert.showEditForm = false;
            this.cert.getData.rowData = [];
            this.user.model.welder_no = this.selectedUserRow.welder_no
            this.user.model.welder_status = this.selectedUserRow.welder_status

            this.onViewFiles(this.selectedUserRow);
            this.loadCertificates(this.selectedUserRow.user_id, this.selectedUserRow.role_id);

            this.$refs.saveModal.show();
        },
        saveAction (e) {
            e.preventDefault();

            let data = {
                welder: null,
                is_welder: 1,
                welder_no: this.user.model.welder_no,
                welder_status: this.user.model.welder_status,
            }

            Object.keys(data).forEach(key => {
                if (data[key] === '') {
                    data[key] = null;
                }
            });

            if (this.selectedUserRow) {
                data['welder'] = this.selectedUserRow.id;
            } else if (this.selectedWelder) {
                data['welder'] = this.selectedWelder.id;
            }

            if (data['welder']) {
                let info = data['welder'];
                info = info.split('|');
                data['role_id'] = info[0];
                http.route('user/' + info[1] + '/roles').post(data, true).then(response => {
                    if (response.isError) {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]))
                        });
                    } else {
                        if (this.selectedUserRow) {
                            this.uploadDocuments(info[1]).then(res => {
                                if (typeof res == 'undefined' || res.isError == false) {
                                    this.selectedUserRow = this.selectedWelder;
                                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                                    this.$refs.saveModal.hide();
                                } else {
                                    Object.keys(res.errorMessage).forEach(key => {
                                        this.errorMsg(Vue.i18n.translate(res.errorMessage[key]));
                                    });
                                }
                            });
                        } else if (this.selectedWelder) {
                            this.selectedUserRow = this.selectedWelder;
                            this.selectedWelder = null;
                            this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.created'));
                            this.showEditWelder();
                        }
                        
                        this.onLoadTable();
                    }
                });
            } else {
                this.errorMsg(Vue.i18n.translate('errors.user_does_not_exist'));
            }
        },
        async uploadDocuments (id) {
            return new Promise((resolve, reject) => {
                if (this.hasFilesCreatePermission && typeof this.$refs.document != 'undefined') {
                    var file = this.$refs.document.getQueuedFiles();
                    for (let order = 0; order < file.length; order++) {
                        var formData = new FormData();
                        formData.append('file', file[order]);
                        formData.append('type', 'welder');
                        formData.append('welder_id', id);
                        let data = http.route('file').post(
                            formData,
                                {
                                headers: {
                                    'Content-Type': 'multipart/form-data'
                                }
                            }
                        ).then(response => {
                            if (!response.isError) {
                                this.successMsg(Vue.i18n.translate('messages.file_uploaded'));
                            } else {
                                Object.keys(response.errorMessage).forEach(key => {
                                    this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                                });
                            }
                        });
                    }
                }
                
                resolve();
            });
        },
        onViewFiles (row) {
            let info = row.id;
            info = info.split('|');
            this.user.getData.getFiles.length = 0;
            let data = http.route('file?get_all=1&filters[welder_id]=' + info[1] + '&filters[type]=welder').get();
            data.then(response => {
                if(response.data == false) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.user.getData.getFiles = response.data;
                }
            });
        },
        onDownloadFile (id) {
            let data = http.route('file/' + id).get(
            {
                export_pdf: 1
            }, true);
            data.then(response => {
                if (!response.isError) {
                    this.successMsg(Vue.i18n.translate('messages.file.file_download'));

                    let data = http.route('get_content/file/'+response.data.id+'/'+response.data.file_name).downloadPdf({}, response.data.file_name).then(res => {});
                } else {
                    this.errorMsg(Vue.i18n.translate('messages.file.error_download'));
                }
            });
        },
        onDeleteRow (row, index) {
            this.actionTitleDelete = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated + ': ' + row.firstname + ' ' + row.lastname;
            this.delete_message = Vue.i18n.translate('messages.delete_welder');
            this.selectedUserRow = row;
            this.$refs.deleteModal.show();
        },
        onDeleteFile (id) {
            this.actionTitleDelete = Vue.i18n.translate('messages.delete_file_title');
            this.delete_message = Vue.i18n.translate('messages.delete_file');
            this.user.file_id = id;
            this.user.isFileDelete = true;
            this.$refs.deleteModal.show();
        },
        deleteAction (button) {
            if (button == 1) {
                if (this.user.isFileDelete == true) {
                    http.route('file/' + this.user.file_id).delete().then(response => {
                        if (!response.isError) {
                            this.$refs.saveModal.hide();
                            this.onLoadTable();
                            this.successMsg(Vue.i18n.translate('messages.deleted_file_message'));
                        } else {
                            this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                        }
                    });
                } else {
                    let info = this.selectedUserRow.id;
                    info = info.split('|');

                    let data = http.route('user/' + info[1] + '/roles').post({
                        welder: false,
                        role_id: info[0],
                    }, true);
                    data.then(response => {
                        if (response.isError) {
                            this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                        } else {
                            this.selectedWelder = null;
                            this.selectedUserRow = null;

                            if (this.currentPage > 1 && this.user.getData.rowData.length == 1) {
                                this.currentPage = this.currentPage - 1;
                            }

                            this.onLoadTable();
                            this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                        }
                    });
                }
            }
            this.$refs.deleteModal.hide();
        },
        onExportButton() {
            http.route(this.tableName + '/getall?get_all_welders=1&get_all=1&filters[is_welder]=1').exportExcel({
                export_welder: 1,
                search: this.searchInput,
                sort_by: this.sortBy,
                sort_order: this.sortType
            }, 'welders.xlsx').then(response => {
                if (typeof response != 'undefined') {
                    this.errorMsg(Vue.i18n.translate('errors.error_export'));
                } else {
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.exported'));
                }
            });
        },
        onImportButton(fieldName, fileList) {
            const formData = new FormData();

            if (!fileList.length) return;

            Array
            .from(Array(fileList.length).keys())
            .map(x => {
                formData.append(fieldName, fileList[x], fileList[x].name);
            });

            http.route(this.tableName+'/import_welder').importExcel(formData)
            .then(response => {
                if (response.isError) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.onLoadTable();
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.imported'));
                }
            });
        },
        //Certificates
        loadCertificates(forUser, forRole) {
            let data = http.route('qualificationcertificate?filters[for_user]='+forRole+'|'+forUser).get();
            data.then(response => {
                if (!response.isError) {
                    var data = response.data;
                    data.forEach(field => {
                        if (field.certificate_valid_days == 1) {
                            field.certificate_valid_days = field.certificate_valid_days + ' ' + Vue.i18n.translate('table.day');
                        } else if (field.certificate_valid_days > 1) {
                            field.certificate_valid_days = field.certificate_valid_days + ' ' + Vue.i18n.translate('table.days');
                        } else if (field.certificate_valid_days == 'expired') {
                            field.certificate_valid_days = Vue.i18n.translate('misc.expired');
                        } else {
                            field.certificate_valid_days = null;
                        }
                    });
                    this.cert.getData.rowData = data;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]))
                    });
                }
            });
        },
        onAddCertificateRow() {
            this.cert.model = {
                certificate_no: null,
                user_id: null,
                qualification: null,
                number: null,
                welding_process: null,
                welding_type: null,
                group_filler_material: null,
                range_of_filler_material: null,
                wps_id: null,
                material: null,
                dia: null,
                thickness: null,
                remark: null,
                expire_at: null,
                expire_remark: null,
                selectedWps: null,
            };
            this.actionTitleCertificate = Vue.i18n.translate('general.users.welders.certificate_add') + this.selectedUserRow.fullname;
            this.$refs.addCertificateModal.show();
        },
        onAddCertificateHidden() {
            this.onEditRow(this.selectedUserRow);
        },
        onEditCertificateRow (row) {
            this.selectedCertRow = row;
            this.cert.model.certificate_no = row.certificate_no;
            this.cert.model.user_id = row.user_id;
            this.cert.model.qualification = row.qualification;
            this.cert.model.number = row.number;
            this.cert.model.welding_process = row.welding_process;
            this.cert.model.welding_type = row.welding_type;
            this.cert.model.group_filler_material = row.group_filler_material;
            this.cert.model.range_of_filler_material  = row.range_of_filler_material;
            this.cert.model.wps_id  = row.wps_id;
            this.cert.model.material = row.material;
            this.cert.model.dia = row.dia;
            this.cert.model.thickness = row.thickness;
            this.cert.model.remark = row.remark;
            this.cert.model.expire_at = row.expire_at;
            this.cert.model.expire_remark = row.expire_remark;
            this.cert.model.selectedWps = row.wps;
            this.cert.showEditForm = true;
        },
        onDeleteCertificateRow (row) {
            this.selectedCertRow = row;
            this.actionTitleCertificate = Vue.i18n.translate('general.users.welders.certificate_delete');
            this.$refs.deleteCertificateModal.show();
        },
        actionUpdateCertificate(e) {
            e.preventDefault();
            let info = this.selectedUserRow.id;
            info = info.split('|');
            if (this.cert.model.expire_at == '') {
                this.cert.model.expire_at = null;
            }

            let put_data = {
                user_id: info[1],
                role_id: info[0],

                certificate_no: this.cert.model.certificate_no,
                qualification: this.cert.model.qualification,
                number: this.cert.model.number,
                welding_process: this.cert.model.welding_process,
                welding_type: this.cert.model.welding_type,
                group_filler_material: this.cert.model.group_filler_material,
                range_of_filler_material: this.cert.model.range_of_filler_material,
                wps_id: this.cert.model.wps_id,
                material: this.cert.model.material,
                dia: this.cert.model.dia,
                thickness: this.cert.model.thickness,
                remark: this.cert.model.remark,
                expire_at: this.cert.model.expire_at,
                expire_remark: this.cert.model.expire_remark,
            };

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            let data = http.route("qualificationcertificate/"+this.selectedCertRow.id).patch(put_data, true);
            data.then(response => {
                if (response.isError) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.successMsg(Vue.i18n.translate('general.users.welders.certificate_updated'));
                    this.cert.showEditForm = false;
                    this.loadCertificates(this.selectedUserRow.user_id, this.selectedUserRow.role_id);
                    this.onLoadTable();
                }
            });
        },
        actionInsertCertificate(e) {
            this.insertCertificate(this.selectedUserRow.id, false, e);
        },
        actionDeleteCertificate(button) {
            if(button == 1) {
                var id = this.selectedCertRow.id;
                let data = http.route("qualificationcertificate/" + id).delete();
                data.then(response => {
                    if (response.data != false) {
                        // if (this.currentPage > 1 && this.getData.rowData.length == 1) {
                        //     this.currentPage = this.currentPage - 1;
                        // }
                        let info = this.selectedUserRow.id;
                        info = info.split('|');
                        this.loadCertificates(info[1], info[0]);
                        this.successMsg(Vue.i18n.translate('general.users.welders.certificate_removed'));
                        this.onLoadTable();
                    }
                });
                this.selectedCertRow = null;
            }
            this.$refs.deleteCertificateModal.hide();
            this.onEditRow(this.selectedUserRow);
        },
        insertCertificate(forUserid, loadAfterInsert=true, e) {
            e.preventDefault();
            let info = forUserid;
            info = info.split('|');

            let put_data = {
                user_id: info[1],
                role_id: info[0],

                certificate_no: this.cert.model.certificate_no,
                qualification: this.cert.model.qualification,
                number: this.cert.model.number,
                welding_process: this.cert.model.welding_process,
                welding_type: this.cert.model.welding_type,
                group_filler_material: this.cert.model.group_filler_material,
                range_of_filler_material: this.cert.model.range_of_filler_material,
                wps_id: this.cert.model.wps_id,
                material: this.cert.model.material,
                dia: this.cert.model.dia,
                thickness: this.cert.model.thickness,
                remark: this.cert.model.remark,
                expire_at: this.cert.model.expire_at,
                expire_remark: this.cert.model.expire_remark,
            };

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            let data = http.route("qualificationcertificate").post(put_data, true);
            data.then(response => {
                if (response.isError) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.successMsg(Vue.i18n.translate('general.users.welders.certificate_added'));
                    if(loadAfterInsert) {
                        this.cert.showEditForm = false;
                        this.loadCertificates(info[1], info[0]);
                    }
                    this.onLoadTable();
                    this.$refs.addCertificateModal.hide();
                }
            });
        },
        updateFields(id){
            let data = http.route('wps/' + id).get();
            data.then(response => {
                if (response.data != false) {
                    let resp = response.data;
                    this.cert.model.welding_type = resp.weld_type;
                    this.cert.model.group_filler_material = resp.filler_material;
                    this.cert.model.dia = resp.dia;
                    this.cert.model.thickness = resp.thickness;
                }
            });
        },
        onExportButtonCert() {
            http.route(this.tableName + '/getall?get_all=1&filters[welder]=1').exportExcel({
                    export: 1,
                    search: this.searchInput,
                    sort_by: this.sortBy,
                    sort_order: this.sortType
                }, 'welderscertificates.xlsx').then(response => {
                if (typeof response != 'undefined') {
                    this.errorMsg(Vue.i18n.translate('errors.error_export'));
                } else {
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.exported'));
                }
            });
        },
        onWelderSearch(search) {
            if (search == '') {
                this.currentWelder = [];
                
                return false;
            }

            this.isWelderLoading = true;
            this.searchWelder(search, this);
        },
        searchWelder: _.debounce((search, vm) => {
            let data = http.route('user/getall?get_all_welders=1&search_select=' + search + '&filters[is_welder]=0').get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentWelder = response.data;
                }
                vm.isWelderLoading = false;
            });
        }, 500),
        clearSelectedWelder () {
            this.selectedWelder = null;
        },
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
	select {
		-webkit-appearance:menulist-button;
		-moz-appearance:menulist-button;
	}
</style>




// WEBPACK FOOTER //
// src/components/pages/users/welders.vue